import type { PricingBlock as PricingBlockType, LineItem, Currency } from "@/types";
import { computeTotals, formatMoney } from "@/lib/money";
import { resolveTokens } from "@/lib/lineItems";
import { useRenderer } from "../context";

/**
 * "Build your quote" — the quote's line items, the single source of truth for
 * money (spec §5.4). Reads lineItems + currency/VAT + the optional selection
 * from renderer context; never reads amounts from block props. Matches the CD
 * layout exactly: a single bordered box of rows ending in the dark "Total
 * investment" bar. Required rows first, then optional rows with a toggle
 * (filled "✓ Selected" / outline "Add to quote"). Each row may carry an
 * Included / Not-included pair (each column renders only when non-empty).
 */
export function PricingBlock({ block }: { block: PricingBlockType }) {
  const { lineItems, quote, selectedOptionalIds, onToggleOptional } = useRenderer();
  const currency = quote?.currency ?? "GBP";
  const vatTreatment = quote?.vat_treatment || "inclusive";
  const shortDescription = quote?.project_description;

  const totals = computeTotals(lineItems, vatTreatment, selectedOptionalIds);
  const hasItems = totals.required.length > 0 || totals.optional.length > 0;
  const hasOptional = totals.optional.length > 0;
  const selected = new Set(selectedOptionalIds);
  const rows = [...totals.required, ...totals.optional];

  const totalNote =
    hasOptional && totals.selectedOptionalTotal > 0
      ? `Base ${formatMoney(totals.subtotal, currency)} + ${formatMoney(
          totals.selectedOptionalTotal,
          currency,
        )} optional · ${totals.vatLabel}`
      : totals.vatLabel;

  return (
    <section className="block block--pricing qr-pricing" id="pricing">
      <p className="eyebrow">Project investment</p>
      <h2 className="qr-pricing__title">{block.props.heading ?? "Build your quote"}</h2>
      <p className="qr-pricing__lede">
        It’s not a fee — it’s an investment in realising the home of your dreams. Select the optional
        services you’d like to include and your total updates automatically.
      </p>

      {!hasItems ? (
        <p className="block--pricing__empty">No line items have been added to this quote.</p>
      ) : (
        <div className="qr-pricing__box">
          {rows.map((li) => (
            <PricingRow
              key={li.id}
              item={li}
              currency={currency}
              shortDescription={shortDescription}
              selected={selected.has(li.id)}
              onToggle={li.is_optional ? () => onToggleOptional(li.id) : undefined}
            />
          ))}

          <div className="qr-pricing__total">
            <div className="qr-pricing__total-label">
              <p className="eyebrow qr-pricing__total-eyebrow">Total investment</p>
              <p className="qr-pricing__total-note">{totalNote}</p>
            </div>
            <div className="num numeric qr-pricing__total-amount">
              {formatMoney(totals.total, currency)}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function PricingRow({
  item,
  currency,
  shortDescription,
  selected,
  onToggle,
}: {
  item: LineItem;
  currency: Currency;
  shortDescription?: string;
  selected: boolean;
  onToggle?: () => void;
}) {
  const optional = item.is_optional;
  // {{short_description}} and friends fill in here, at render time (§ token support).
  const title = resolveTokens(item.title, shortDescription);
  const description = resolveTokens(item.description, shortDescription);
  const included = (item.included ?? [])
    .map((s) => resolveTokens(s, shortDescription))
    .filter((s) => s && s.trim().length > 0);
  const notIncluded = (item.not_included ?? [])
    .map((s) => resolveTokens(s, shortDescription))
    .filter((s) => s && s.trim().length > 0);
  const hasLists = included.length > 0 || notIncluded.length > 0;

  return (
    <div className={`qr-prow${optional && selected ? " qr-prow--selected" : ""}`}>
      <div className="qr-prow__main">
        <div className="qr-prow__head">
          <h3 className="qr-prow__title">{title}</h3>
          <span className={`cd-badge${optional ? "" : " cd-badge--slate"}`}>
            {optional ? "Optional" : "Required"}
          </span>
        </div>
        {description ? <p className="qr-prow__desc">{description}</p> : null}

        {hasLists ? (
          <div className="qr-prow__lists">
            {included.length > 0 ? (
              <div className="qr-prow__list">
                <p className="eyebrow qr-prow__list-label qr-prow__list-label--incl">Included</p>
                <div className="qr-prow__list-items">
                  {included.map((s, i) => (
                    <div key={i}>{s}</div>
                  ))}
                </div>
              </div>
            ) : null}
            {notIncluded.length > 0 ? (
              <div className="qr-prow__list">
                <p className="eyebrow qr-prow__list-label qr-prow__list-label--not">Not included</p>
                <div className="qr-prow__list-items">
                  {notIncluded.map((s, i) => (
                    <div key={i}>{s}</div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="qr-prow__aside">
        <div className="num numeric qr-prow__price">{formatMoney(item.line_total, currency)}</div>
        {optional ? (
          <button
            type="button"
            className={`qr-pricing__toggle${selected ? " qr-pricing__toggle--on" : ""}`}
            aria-pressed={selected}
            onClick={onToggle}
          >
            {selected ? "✓ Selected" : "Add to quote"}
          </button>
        ) : (
          <span className="qr-prow__always small">Always included</span>
        )}
      </div>
    </div>
  );
}
